import { z } from 'zod'
import type { Catalog } from '@/api/catalog'
import { filamentOptions } from '@/api/catalog'
import type { Pool } from '@/api/pools'

// The guided Product setup: a wizard to create a Product and a hub to manage one, shown instead of
// the classic screens when the `product_setup` feature flag is on for the User or the Workspace
// (Flipper, /admin/flipper).
export const SETUP_STEPS = ['product', 'models', 'variants', 'bom', 'done'] as const
export type SetupStep = (typeof SETUP_STEPS)[number]
// Once the Product exists, the wizard lives on the Product route as `?setup=<step>`.
export const setupSearch = z.enum(SETUP_STEPS).optional().catch(undefined)

// Today in the Workspace timezone, as the server counts it.
export function todayIn(timezone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date())
}

const PRODUCT_CODE_MAX = 10
const SKU_MAX = 20

function letters(value: string): string[] {
  return value
    .normalize('NFD')
    .toUpperCase()
    .split(/[^A-Z0-9]+/)
    .filter(Boolean)
}

// A short code from the name: "Kucing Gantungan" → "KUG", "Vas" → "VAS", "Gantungan Kunci Kucing"
// → "GKK". Only a suggestion; the Owner can type any code.
export function suggestCode(name: string): string {
  const words = letters(name)
  if (words.length === 0) return ''
  if (words.length === 1) return words[0]!.slice(0, 3)
  const initials = words.map((word) => word[0]).join('')
  const code = initials.length === 2 ? `${words[0]!.slice(0, 2)}${initials[1]}` : initials
  return code.slice(0, PRODUCT_CODE_MAX)
}

// "<code>-<color>" for a Variant named after its color: KUG + "Merah Muda" → KUG-MERAHMUDA.
export function suggestSku(code: string, name: string): string {
  const color = letters(name).join('')
  return color ? `${code}-${color}`.slice(0, SKU_MAX) : `${code}-`
}

// Common color words typed in Indonesian, for a color the catalog doesn't name (it names colors in
// English, as the brands do). The first word that matches wins: "Biru Muda" → biru.
const COLOR_WORDS: Record<string, string> = {
  hitam: '#000000',
  putih: '#FFFFFF',
  abu: '#8E9089',
  perak: '#C0C0C0',
  merah: '#D32F2F',
  oranye: '#FF7A00',
  jingga: '#FF7A00',
  kuning: '#FFD600',
  hijau: '#2E9E4F',
  biru: '#1E4FC2',
  ungu: '#6A3DB8',
  pink: '#F48FB1',
  coklat: '#7B4A2A',
  cokelat: '#7B4A2A',
  krem: '#F1E3C6',
  emas: '#C9A227',
  transparan: '#E8F1F5',
}

// A filament Pool's swatch: the catalog's hex for its color name (any case), else a common color
// word in it; undefined when neither knows the color.
export function poolHex(catalog: Catalog | undefined, pools: Pool[], pool: Pool | undefined): string | undefined {
  if (!pool || pool.kind !== 'filament' || !pool.color) return undefined
  const color = pool.color.trim().toLowerCase()
  for (const [name, hex] of filamentOptions(catalog, pools, pool).colorHex) {
    if (name.toLowerCase() === color) return hex
  }
  const word = color.split(/\s+/).find((part) => Object.hasOwn(COLOR_WORDS, part))
  return word ? COLOR_WORDS[word] : undefined
}
